import React, { FC, useState, useEffect } from 'react'
import { Box, Typography, Paper } from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { PortalLayout } from '@/apps/legacy/components/portal/PortalLayout'
import { PharmacySelector } from '@/apps/legacy/components/portal/PharmacySelector'
import {
  usePharmacyListQuery,
  usePharmacyUrlParam,
} from '@/features/internal/metrics'
import dayjs, { Dayjs } from 'dayjs'
import { useLocation, useNavigate } from 'react-router-dom'
import { OverviewTab } from './components/OverviewTab'
import { TrendsTab } from './components/TrendsTab'
import { ComparisonTab } from './components/ComparisonTab'
import { colorTokens } from '@/apps/legacy/shared/styles/colorTokens'
import { Button } from '@mui/material'

// モード定義
const modes = [
  { id: 'overview', label: '利用状況確認' },
  { id: 'trends', label: '実績推移' },
  { id: 'comparison', label: '薬局別比較' },
]

export const MetricsUsagePage: FC = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const searchParams = new URLSearchParams(location.search)
  const tabParam = searchParams.get('tab')

  const { pharmacyId: selectedPharmacyId, updatePharmacyParam } =
    usePharmacyUrlParam()
  const [activeTab, setActiveTab] = useState(tabParam || 'overview')
  const [startDate, setStartDate] = useState<Dayjs | null>(
    dayjs().subtract(1, 'month')
  )
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs())

  const { pharmacyData } = usePharmacyListQuery()

  // 初期選択値を設定
  useEffect(() => {
    if (pharmacyData && !selectedPharmacyId) {
      const initialValue = pharmacyData.has_headquarters_permission
        ? '' // 全社
        : pharmacyData.pharmacies.length > 0
        ? pharmacyData.pharmacies[0].id
        : pharmacyData.current_pharmacy_id
      updatePharmacyParam(initialValue)
    }
  }, [pharmacyData, selectedPharmacyId, updatePharmacyParam])

  // タブ切り替え処理
  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId)
    const newSearchParams = new URLSearchParams(location.search)
    newSearchParams.set('tab', tabId)
    navigate(`${location.pathname}?${newSearchParams.toString()}`, {
      replace: true,
    })
  }

  const handlePharmacyChange = (pharmacyId: string) => {
    updatePharmacyParam(pharmacyId)
  }

  // タブコンテンツをレンダリング
  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <OverviewTab
            startDate={startDate}
            endDate={endDate}
            selectedPharmacyId={selectedPharmacyId}
          />
        )
      case 'trends':
        return (
          <TrendsTab
            startDate={startDate}
            endDate={endDate}
            selectedPharmacyId={selectedPharmacyId}
          />
        )
      case 'comparison':
        return <ComparisonTab startDate={startDate} endDate={endDate} />
      default:
        return null
    }
  }

  return (
    <PortalLayout>
      {/* ページタイトル & モード選択 */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          mb: 4,
          flexWrap: 'wrap',
          gap: 3,
        }}
      >
        <Typography
          variant="h5"
          sx={{
            color: colorTokens.text.primary,
          }}
        >
          利用統計
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {modes.map((mode) => (
            <Button
              key={mode.id}
              variant={activeTab === mode.id ? 'contained' : 'outlined'}
              size="small"
              onClick={() => handleTabChange(mode.id)}
              sx={[
                {
                  borderRadius: 2,
                  px: 2,
                  py: 1,
                  minWidth: 'auto',
                },
                activeTab === mode.id
                  ? {
                      backgroundColor: colorTokens.blue[600],
                      color: colorTokens.background.paper,
                      border: `1px solid ${colorTokens.blue[600]}`,
                      '&:hover': {
                        backgroundColor: colorTokens.blue[700],
                      },
                    }
                  : {
                      color: colorTokens.blue[600],
                      borderColor: colorTokens.blue[600],
                      backgroundColor: colorTokens.background.paper,
                      '&:hover': {
                        backgroundColor: colorTokens.action.primaryBgLight,
                        borderColor: colorTokens.blue[600],
                        color: colorTokens.blue[600],
                      },
                    },
              ]}
            >
              {mode.label}
            </Button>
          ))}
        </Box>
      </Box>

      {/* 共通フィルターエリア */}
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          backgroundColor: colorTokens.background.paper,
          borderRadius: 2,
          border: `1px solid ${colorTokens.border.light}`,
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          p: 3,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          {/* 薬局選択は薬局別比較タブ以外で表示 */}
          {activeTab !== 'comparison' && (
            <Box sx={{ minWidth: 200 }}>
              <PharmacySelector
                value={selectedPharmacyId}
                onChange={handlePharmacyChange}
                pharmacyData={pharmacyData}
                size="small"
                label="店舗"
                showAllOption={true}
              />
            </Box>
          )}

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="開始日"
              value={startDate}
              onChange={setStartDate}
              slotProps={{
                textField: {
                  size: 'small',
                  sx: { width: 200 },
                },
              }}
            />
          </LocalizationProvider>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="終了日"
              value={endDate}
              onChange={setEndDate}
              slotProps={{
                textField: {
                  size: 'small',
                  sx: { width: 200 },
                },
              }}
            />
          </LocalizationProvider>
        </Box>
      </Paper>

      {/* タブコンテンツ */}
      {renderTabContent()}
    </PortalLayout>
  )
}
